const URL_PATTERN =
  /(?:\b(?:https?:\/\/|www\.)\S+|\b(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}(?:\/\S*)?)/giu;
const SOCIAL_TOKEN_PATTERN = /(^|\s)[@#][^\s@#]+/gu;
const LETTER_PATTERN = /\p{L}/u;
const TOKEN_PATTERN = /\p{L}+/gu;
const MAX_URL_LETTER_SHARE = 0.35;
const MIN_LETTER_DENSITY = 0.7;
const MIN_ENGLISH_LETTERS = 14;
const MIN_GREEK_LETTERS = 10;
const MIN_BULGARIAN_LETTERS = 18;
const MIN_DOMINANT_SCRIPT_SHARE = 0.85;
const MIN_BULGARIAN_SCRIPT_SHARE = 0.9;

const ENGLISH_FUNCTION_WORDS = new Set(
  "a an and are as at be been but by for from had has have i in is it its my of on our that the their they this to was we were with you your".split(
    " "
  )
);
const ENGLISH_LEXICAL_WORDS = new Set(
  "another angler bait bass beautiful biggest boat carp catch caught evening finally first fish fishing great hook huge lake landed morning nice perch pike released reel river salmon sea shore today tomorrow trout water yesterday".split(
    " "
  )
);
const NON_ENGLISH_LATIN_MARKERS = new Set(
  "amigos avec captura con das der des die el fang heute hoy la las les los mis mit para pesca peixe poisson río und une una".split(
    " "
  )
);
const BULGARIAN_STRONG_WORDS = new Set(
  "във днес добра добре добър към риба рибата сме сте страхотен страхотна съм със тази тези това този хванах хванахме ще".split(
    " "
  )
);
const BULGARIAN_SUPPORTING_WORDS = new Set(
  "голям голяма езеро езерото край много море морето река реката улов улова".split(
    " "
  )
);
const RUSSIAN_ONLY_LETTERS = /[ыэё]/u;
function containsNonAsciiCharacter(value) {
  return Array.from(value).some(
    (character) => character.codePointAt(0) > 0x7f
  );
}

function isGreekCodePoint(codePoint) {
  return (
    (codePoint >= 0x0370 && codePoint <= 0x03ff) ||
    (codePoint >= 0x1f00 && codePoint <= 0x1fff)
  );
}

function isCyrillicCodePoint(codePoint) {
  return codePoint >= 0x0400 && codePoint <= 0x052f;
}

function isLatinCodePoint(codePoint) {
  return (
    (codePoint >= 0x0041 && codePoint <= 0x005a) ||
    (codePoint >= 0x0061 && codePoint <= 0x007a) ||
    (codePoint >= 0x00c0 && codePoint <= 0x024f) ||
    (codePoint >= 0x1e00 && codePoint <= 0x1eff)
  );
}

function letterCount(value) {
  let count = 0;
  for (const character of value) {
    if (LETTER_PATTERN.test(character)) count += 1;
  }
  return count;
}

function distinctMatches(tokens, vocabulary) {
  return new Set(tokens.filter((token) => vocabulary.has(token))).size;
}

function analyzeCaption(caption) {
  if (typeof caption !== "string" || !caption.trim()) return null;

  const normalized = caption.normalize("NFKC").toLowerCase();
  const originalLetters = letterCount(normalized);
  if (!originalLetters) return null;

  const withoutUrls = normalized.replace(URL_PATTERN, " ");
  const lettersAfterUrls = letterCount(withoutUrls);
  if (
    (originalLetters - lettersAfterUrls) / originalLetters >
    MAX_URL_LETTER_SHARE
  ) {
    return null;
  }

  const content = withoutUrls.replace(SOCIAL_TOKEN_PATTERN, " ").trim();
  const tokens = content.match(TOKEN_PATTERN) || [];
  if (!tokens.length) return null;

  let latinLetters = 0;
  let greekLetters = 0;
  let cyrillicLetters = 0;
  let totalLetters = 0;
  let nonSpaceCharacters = 0;
  for (const character of content) {
    if (!/\s/u.test(character)) nonSpaceCharacters += 1;
    if (!LETTER_PATTERN.test(character)) continue;
    totalLetters += 1;
    const codePoint = character.codePointAt(0);
    if (isLatinCodePoint(codePoint)) latinLetters += 1;
    else if (isGreekCodePoint(codePoint)) greekLetters += 1;
    else if (isCyrillicCodePoint(codePoint)) cyrillicLetters += 1;
  }

  if (
    !totalLetters ||
    !nonSpaceCharacters ||
    totalLetters / nonSpaceCharacters < MIN_LETTER_DENSITY
  ) {
    return null;
  }
  return {
    content,
    tokens,
    totalLetters,
    latinLetters,
    greekLetters,
    cyrillicLetters,
  };
}

function detectGreek(analysis) {
  return (
    analysis.totalLetters >= MIN_GREEK_LETTERS &&
    analysis.tokens.length >= 2 &&
    analysis.greekLetters / analysis.totalLetters >= MIN_DOMINANT_SCRIPT_SHARE
  );
}

function detectBulgarian(analysis) {
  if (
    analysis.totalLetters < MIN_BULGARIAN_LETTERS ||
    analysis.tokens.length < 3 ||
    analysis.cyrillicLetters / analysis.totalLetters <
      MIN_BULGARIAN_SCRIPT_SHARE ||
    RUSSIAN_ONLY_LETTERS.test(analysis.content)
  ) {
    return false;
  }

  const strongSignals = distinctMatches(
    analysis.tokens,
    BULGARIAN_STRONG_WORDS
  );
  const supportingSignals = distinctMatches(
    analysis.tokens,
    BULGARIAN_SUPPORTING_WORDS
  );
  const hardSignCount = [...analysis.content].filter(
    (character) => character === "ъ"
  ).length;
  return (
    strongSignals >= 2 ||
    (strongSignals >= 1 &&
      (supportingSignals >= 2 || hardSignCount >= 1)) ||
    (hardSignCount >= 2 && supportingSignals >= 2)
  );
}

function detectEnglish(analysis) {
  if (
    analysis.totalLetters < MIN_ENGLISH_LETTERS ||
    analysis.tokens.length < 3 ||
    analysis.latinLetters / analysis.totalLetters < MIN_DOMINANT_SCRIPT_SHARE ||
    containsNonAsciiCharacter(analysis.content)
  ) {
    return false;
  }

  const foreignSignals = distinctMatches(
    analysis.tokens,
    NON_ENGLISH_LATIN_MARKERS
  );
  if (foreignSignals >= 2) return false;

  const functionSignals = distinctMatches(
    analysis.tokens,
    ENGLISH_FUNCTION_WORDS
  );
  const lexicalSignals = distinctMatches(
    analysis.tokens,
    ENGLISH_LEXICAL_WORDS
  );
  return (
    functionSignals >= 3 ||
    (functionSignals >= 1 && lexicalSignals >= 2) ||
    lexicalSignals >= 3
  );
}

export function detectPostCaptionLanguage(caption) {
  const analysis = analyzeCaption(caption);
  if (!analysis) return null;
  if (detectGreek(analysis)) return "el";
  if (detectBulgarian(analysis)) return "bg";
  if (detectEnglish(analysis)) return "en";
  return null;
}
